import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { KeenIcon } from '@/components';
import { Can } from '@casl/react';
import { useAbility } from '@/providers/';
import { Skeleton } from '@/components/ui/skeleton';
import reminderService from '@/services/reminder/reminderService';
import { ReminderModal } from './ReminderModal';
import { TaskTitle } from './SourceBadge';

/**
 * Dashboard panel listing the current user's pending reminders — overdue first,
 * then upcoming. Also the entry point for creating general (parent-less)
 * reminders.
 */
const RemindersPanel = () => {
  const navigate = useNavigate();
  const { ability } = useAbility();
  const [groups, setGroups] = useState({ overdue: [], upcoming: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const formikRef = useRef();

  const fetchReminders = useCallback(async () => {
    try {
      const { data, status } = await reminderService.getUpcomingReminders();
      if (status) {
        setGroups({ overdue: data.overdue || [], upcoming: data.upcoming || [] });
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Failed to fetch Tasks.', {
        description: 'An error occurred while fetching tasks. Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const handleComplete = async (event, id) => {
    event.stopPropagation();
    try {
      const { status } = await reminderService.completeReminder(id);
      if (status) {
        fetchReminders();
        toast.success('Task Completed', {
          description: 'Task marked complete successfully.',
        });
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Failed to update Task.', {
        description: 'An error occurred while updating the task. Please try again later.',
      });
    }
  };

  const handleCreate = async (formData) => {
    try {
      setCreateLoading(true);
      const { status } = await reminderService.createReminder(formData);
      if (status) {
        setOpen(false);
        fetchReminders();
        toast.success('Task Created Successfully', {
          description: 'Task created successfully.',
        });
      }
    } catch (error) {
      console.error('Error', error);
      if (error.message == '422 Unprocessable Entity. Validation failed.') {
        const formattedErrors = Object.fromEntries(
          Object.entries(error.errors).map(([key, value]) => [key, value[0]])
        );
        formikRef.current?.setErrors(formattedErrors);
        toast.error('Validation Error.', {
          description: 'Please check your input. Some fields may be invalid.',
        });
        return;
      }
      toast.error('Oops! Something went wrong.', {
        description: 'An error occurred while creating the task. Please try again in a moment.',
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const parentLabel = (reminder) => {
    const { remindable, remindable_alias } = reminder;
    if (!remindable || !remindable_alias) return 'General';
    return remindable_alias === 'contact'
      ? `${remindable.first_name || ''} ${remindable.last_name || ''}`.trim() || 'Contact'
      : remindable.name || 'Matter';
  };

  const handleRowClick = (reminder) => {
    const { remindable, remindable_alias } = reminder;
    if (!remindable || !remindable_alias) return;

    navigate(remindable_alias === 'contact'
      ? `/contacts/${remindable.id}?tab=Reminders`
      : `/matters/detail/${remindable.id}?tab=Reminders`);
  };

  const renderRow = (reminder, isOverdue) => (
    <div
      key={reminder.id}
      onClick={() => handleRowClick(reminder)}
      className="flex items-start gap-2.5 py-2.5 border-b border-gray-200 last:border-b-0 cursor-pointer"
    >
      <input
        type="checkbox"
        className="checkbox checkbox-sm mt-0.5"
        title="Mark complete"
        onClick={(event) => handleComplete(event, reminder.id)}
        readOnly
      />
      <div className="grow">
        <div className="text-sm font-medium text-gray-900">
          <TaskTitle reminder={reminder} />
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-0.5">
          <span className={`text-xs ${isOverdue ? 'text-red-600' : 'text-gray-600'}`}>
            {reminder.due_at}
          </span>
          <span className="text-xs text-gray-500">· {parentLabel(reminder)}</span>
        </div>
      </div>
    </div>
  );

  const isEmpty = !groups.overdue.length && !groups.upcoming.length;

  return (
    <div className="card w-full">
      <div className="card-header px-3.5">
        <h3 className="card-title">Tasks</h3>
        <div className="flex items-center flex-wrap gap-2">
          {/* This panel only shows pending reminders assigned to you — the full
              list (completed, delegated, general) lives on /reminders. */}
          <button className="text-xs btn btn-sm btn-clear btn-primary" onClick={() => navigate('/reminders')}>
            View All
          </button>
          <Can I="handle" a="reminder" ability={ability}>
            <button className="text-xs btn btn-sm btn-light" onClick={() => setOpen(true)}>
              <KeenIcon icon="plus" className="w-fit btn-icon" />
              New Task
            </button>
          </Can>
        </div>
      </div>

      <div className="card-body px-3.5 pt-4 pb-3 overflow-auto max-h-[30rem] custom-scrollbar">
        {loading ? (
          <div className="flex flex-col gap-4">
            {[1, 2, 3, 4, 5].map((row) => (
              <Skeleton key={row} className="h-8 w-full" />
            ))}
          </div>
        ) : isEmpty ? (
          <div className="text-sm text-gray-600 italic">
            Nothing pending assigned to you. Completed and delegated tasks are on the{' '}
            <span className="link cursor-pointer not-italic" onClick={() => navigate('/reminders')}>
              Tasks page
            </span>.
          </div>
        ) : (
          <>
            {groups.overdue.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold uppercase text-red-600 mb-1">Overdue</div>
                {groups.overdue.map((reminder) => renderRow(reminder, true))}
              </div>
            )}
            {groups.upcoming.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase text-gray-600 mb-1">Upcoming</div>
                {groups.upcoming.map((reminder) => renderRow(reminder, false))}
              </div>
            )}
          </>
        )}
      </div>

      <ReminderModal
        open={open}
        onClose={() => setOpen(false)}
        mode="create"
        data={null}
        onSubmit={handleCreate}
        createLoading={createLoading}
        ref={formikRef}
      />
    </div>
  );
};

export { RemindersPanel };
